/**
 * Payment terms utility functions
 */

/**
 * Parse payment terms string and return number of days
 * @param {string} paymentTerms - Payment terms string (e.g., "net-30", "Net 15", "30 days")
 * @returns {number} Number of days
 */
export const parsePaymentTermsToDays = (paymentTerms) => {
  if (!paymentTerms) return 30; // Default to 30 days
  
  const terms = paymentTerms.toLowerCase().trim();
  
  // Handle "net-XX" format (e.g., "net-30", "net-15")
  if (terms.includes('net-')) {
    const match = terms.match(/net-(\d+)/);
    return match ? parseInt(match[1]) : 30;
  }
  
  // Handle "net XX" format (e.g., "net 30", "Net 15")
  if (terms.includes('net ')) {
    const match = terms.match(/net\s+(\d+)/);
    return match ? parseInt(match[1]) : 30;
  }
  
  // Handle "XX days" format (e.g., "30 days", "15 days")
  const dayMatch = terms.match(/(\d+)\s*days?/);
  if (dayMatch) {
    return parseInt(dayMatch[1]);
  }
  
  // Handle immediate payment terms
  if (terms.includes('immediate') || 
      terms.includes('due on receipt') || 
      terms.includes('cod') ||
      terms.includes('cash on delivery') ||
      terms.includes('payment on delivery')) {
    return 0;
  }
  
  // Handle weekly terms
  if (terms.includes('week')) {
    const weekMatch = terms.match(/(\d+)\s*weeks?/);
    if (weekMatch) {
      return parseInt(weekMatch[1]) * 7;
    }
    return 7; // Default to 1 week
  }
  
  // Handle monthly terms
  if (terms.includes('month')) {
    const monthMatch = terms.match(/(\d+)\s*months?/);
    if (monthMatch) {
      return parseInt(monthMatch[1]) * 30;
    }
    return 30; // Default to 1 month
  }
  
  // Try to extract any number from the string as days
  const numberMatch = terms.match(/(\d+)/);
  if (numberMatch) {
    return parseInt(numberMatch[1]);
  }
  
  // Default fallback
  return 30;
};

/**
 * Calculate due date based on issue date and payment terms
 * @param {string} issueDate - Issue date in YYYY-MM-DD format
 * @param {string} paymentTerms - Payment terms string
 * @returns {string} Due date in YYYY-MM-DD format
 */
export const calculateDueDate = (issueDate, paymentTerms) => {
  if (!issueDate) return '';
  
  const days = parsePaymentTermsToDays(paymentTerms);
  const issue = new Date(issueDate);
  const due = new Date(issue);
  due.setDate(due.getDate() + days);
  
  return due.toISOString().split('T')[0];
};

/**
 * Format payment terms for display
 * @param {string} paymentTerms - Payment terms string
 * @returns {string} Formatted payment terms
 */
export const formatPaymentTerms = (paymentTerms) => {
  if (!paymentTerms) return 'Net 30';
  
  const days = parsePaymentTermsToDays(paymentTerms);
  
  if (days === 0) {
    return 'Due on Receipt';
  } else if (days === 7) {
    return 'Net 7';
  } else if (days === 15) {
    return 'Net 15';
  } else if (days === 30) {
    return 'Net 30';
  } else if (days === 60) {
    return 'Net 60';
  } else if (days === 90) {
    return 'Net 90';
  } else {
    return `Net ${days}`;
  }
};

/**
 * Get common payment terms options for dropdowns
 * @returns {Array} Array of payment terms options
 */
export const getPaymentTermsOptions = () => [
  { value: 'due-on-receipt', label: 'Due on Receipt (0 days)' },
  { value: 'net-7', label: 'Net 7 (7 days)' },
  { value: 'net-15', label: 'Net 15 (15 days)' },
  { value: 'net-30', label: 'Net 30 (30 days)' },
  { value: 'net-45', label: 'Net 45 (45 days)' },
  { value: 'net-60', label: 'Net 60 (60 days)' },
  { value: 'net-90', label: 'Net 90 (90 days)' }
];